import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PlatformCard } from "./PlatformCard";
import { platforms } from "@/data/platforms";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface PlatformConnection {
  platform_name: string;
  is_approved: boolean;
  created_at: string;
}

export const PlatformConnections = () => {
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [enabledPlatforms, setEnabledPlatforms] = useState<string[]>([]);

  useEffect(() => {
    fetchConnections();
    fetchEnabledPlatforms();
  }, []);

  const fetchConnections = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('platform_connections')
        .select('*')
        .eq('host_id', user.id);

      if (error) throw error;
      setConnections(data);
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  };

  const fetchEnabledPlatforms = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('enabled_platforms')
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching enabled platforms:', error);
        return;
      }
      
      if (data?.enabled_platforms) {
        setEnabledPlatforms(data.enabled_platforms as string[]);
      }
    } catch (error) {
      console.error('Error fetching enabled platforms:', error);
    }
  };

  const initiateOAuth = async (platformId: string) => {
    setConnectingPlatform(platformId);
    setIsConnecting(true);

    if (platformId === 'airbnb') {
      localStorage.setItem('platformConnectionRedirect', window.location.href);
      
      const clientId = process.env.AIRBNB_CLIENT_ID || 'YOUR_AIRBNB_CLIENT_ID';
      const redirectUri = `${window.location.origin}/auth/callback/airbnb`;
      const scope = 'listings_read listings_write';
      
      const authUrl = `https://www.airbnb.com/oauth2/auth?` +
        `client_id=${clientId}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=code&` +
        `scope=${encodeURIComponent(scope)}`;

      window.location.href = authUrl;
    } else {
      toast({
        title: "Coming Soon",
        description: `${platformId} integration will be available soon.`,
      });
      setIsConnecting(false);
      setConnectingPlatform(null);
    }
  };

  const renderConnectionStatus = (platformId: string) => {
    const connection = connections.find(c => c.platform_name === platformId);
    
    if (!connection) return null;

    if (connection.is_approved) {
      return (
        <Alert className="mt-2 bg-green-50">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Connected</AlertTitle>
          <AlertDescription>
            Successfully connected on {new Date(connection.created_at).toLocaleDateString()}
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <Alert className="mt-2">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Pending Approval</AlertTitle>
        <AlertDescription>
          Your connection is pending admin approval
        </AlertDescription>
      </Alert>
    );
  };

  return (
    <div className="space-y-4">
      {platforms
        .filter(platform => enabledPlatforms.includes(platform.id))
        .map((platform) => (
          <div key={platform.id} className="space-y-2">
            <PlatformCard
              {...platform}
              isConnecting={isConnecting}
              connectingPlatform={connectingPlatform}
              onConnect={initiateOAuth}
            />
            {renderConnectionStatus(platform.id)}
          </div>
        ))}
    </div>
  );
};